import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Users, Bell, NotebookPen, School, GraduationCap, ArrowRight, CalendarDays, HeartHandshake, CalendarRange } from "lucide-react";
import api from "../lib/api";

const FALLBACK = {
  hero_eyebrow: "School timetable SaaS",
  hero_title: "The school week,",
  hero_highlight: "beautifully organised.",
  hero_subtext: "MyTimetable.co.uk lets your school define its day — periods, breaks, lunch — then gives every pupil a simple login to build their own timetable, keep daily notes, and never miss a school notice.",
  hero_image: "https://images.pexels.com/photos/8199663/pexels-photo-8199663.jpeg",
  marquee: ["Schedule layouts", "Teachers, classes & rooms", "Pupil timetables", "Daily notes", "Push notices"],
  features: [
    { title: "Schedule layouts", desc: "Periods, breaks and lunch — set the shape of your school day once.", bg: "#A7E4D3" },
    { title: "Teachers, classes & rooms", desc: "One directory your pupils pick from. No typos, no confusion.", bg: "#FDE68A" },
    { title: "Pupil timetables", desc: "Children build their own week from your pre-defined options.", bg: "#7DD3FC" },
    { title: "Daily notes", desc: "Pupils jot down homework reminders and notes for each day.", bg: "#F9A8D4" },
    { title: "Push notices", desc: "Message the whole school or a single year group in one click.", bg: "#D8B4FE" },
  ],
  footer_text: "Timetables, notes & notices for UK schools.",
};

const FEATURE_ICONS = [CalendarRange, Users, CalendarDays, NotebookPen, Bell, HeartHandshake];

export default function Landing() {
  const [cms, setCms] = useState(FALLBACK);

  useEffect(() => {
    api.get("/cms/landing")
      .then((r) => { if (r.data && r.data.hero_title) setCms({ ...FALLBACK, ...r.data }); })
      .catch(() => {});
  }, []);

  const features = cms.features?.length ? cms.features : FALLBACK.features;
  const marquee = cms.marquee?.length ? cms.marquee : FALLBACK.marquee;

  return (
    <div className="min-h-screen" data-testid="landing-page">
      <header className="border-b-2 border-zinc-900 bg-[#FDFBF7] sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="brand-logo">
            <div className="w-9 h-9 rounded-xl border-2 border-zinc-900 bg-[#A7E4D3] flex items-center justify-center shadow-[3px_3px_0px_0px_#18181B]">
              <Clock className="w-5 h-5" />
            </div>
            <span className="font-display font-black text-2xl tracking-tight">MyTimetable.co.uk</span>
          </div>
          <nav className="flex items-center gap-3">
            <Link to="/parent/auth" data-testid="nav-parent-login" className="nb-btn bg-white px-4 py-2 text-sm">
              <HeartHandshake className="w-4 h-4" /> <span className="hidden sm:inline">Parents</span>
            </Link>
            <Link to="/pupil/auth" data-testid="nav-pupil-login" className="nb-btn bg-white px-4 py-2 text-sm">
              <GraduationCap className="w-4 h-4" /> <span className="hidden sm:inline">Pupil login</span>
            </Link>
            <Link to="/school/auth" data-testid="nav-school-login" className="nb-btn bg-[#1E3A8A] text-white px-4 py-2 text-sm">
              <School className="w-4 h-4" /> <span className="hidden sm:inline">For schools</span>
            </Link>
          </nav>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-16 pb-12 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <span className="nb-label" data-testid="hero-eyebrow">{cms.hero_eyebrow}</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-4 leading-[1.05]" data-testid="hero-heading">
            {cms.hero_title}<br />
            <span className="bg-[#A7E4D3] px-2 border-2 border-zinc-900 inline-block mt-1 shadow-[4px_4px_0px_0px_#18181B]">{cms.hero_highlight}</span>
          </h1>
          <p className="mt-6 text-lg text-zinc-600 max-w-xl leading-relaxed" data-testid="hero-subtext">{cms.hero_subtext}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/school/auth" data-testid="hero-school-cta" className="nb-btn bg-[#1E3A8A] text-white px-6 py-3 text-base">
              Register your school <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/pupil/auth" data-testid="hero-pupil-cta" className="nb-btn bg-[#FDE68A] px-6 py-3 text-base">
              I'm a pupil
            </Link>
            <Link to="/parent/auth" data-testid="hero-parent-cta" className="nb-btn bg-white px-6 py-3 text-base">
              I'm a parent
            </Link>
          </div>
        </div>
        <div className="lg:col-span-5 relative">
          <div className="nb-card overflow-hidden rotate-1 hover:rotate-0 transition-transform duration-300">
            <img src={cms.hero_image} alt="Students studying together" className="w-full h-72 object-cover" data-testid="hero-image" />
            <div className="p-5 border-t-2 border-zinc-900 bg-white">
              <p className="font-display font-bold text-lg">Monday — 1st Year</p>
              <div className="mt-3 space-y-2">
                {[["1st Period · 08:55", "Maths · Mrs. Smith · Room 1", "#FCA5A5"], ["2nd Period · 09:45", "Science · Mr. Jones · Lab A", "#86EFAC"], ["Break · 10:35", "15 minutes", "#FDE68A"], ["3rd Period · 10:50", "English · Miss Patel · Room 3", "#FDE047"]].map(([t, s, c]) => (
                  <div key={s} className="flex items-center gap-3 rounded-lg border-2 border-zinc-900 px-3 py-2" style={{ backgroundColor: c }}>
                    <span className="text-xs font-bold">{t}</span>
                    <span className="text-sm font-semibold">{s}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y-2 border-zinc-900 bg-white overflow-hidden py-4">
        <div className="flex gap-8 whitespace-nowrap animate-[marquee_24s_linear_infinite]" style={{ width: "max-content" }}>
          {[...marquee, ...marquee].map((m, i) => (
            <span key={i} className="inline-flex items-center gap-2 font-display font-bold text-zinc-700">
              {m} <span className="text-zinc-300 mx-2">✦</span>
            </span>
          ))}
        </div>
        <style>{`@keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" data-testid="features-heading">
          Everything a school day needs
        </h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((f, i) => {
            const Icon = FEATURE_ICONS[i % FEATURE_ICONS.length];
            return (
              <div key={f.title + i} className="nb-card p-6 hover:-translate-y-1 transition-transform duration-200" data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="w-12 h-12 rounded-xl border-2 border-zinc-900 flex items-center justify-center mb-4" style={{ backgroundColor: f.bg || "#A7E4D3" }}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="font-display font-bold text-xl">{f.title}</h3>
                <p className="mt-2 text-zinc-600 text-sm leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
          <div className="nb-card p-6 bg-[#1E3A8A] text-white flex flex-col justify-between hover:-translate-y-1 transition-transform duration-200">
            <div>
              <h3 className="font-display font-bold text-xl">Ready in minutes</h3>
              <p className="mt-2 text-blue-100 text-sm leading-relaxed">Register, set your schedule, share one join code with your pupils. That's it.</p>
            </div>
            <Link to="/school/auth" data-testid="features-cta" className="nb-btn bg-[#FDE68A] text-zinc-900 px-4 py-2 mt-6 self-start text-sm">
              Get started <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-16" data-testid="standalone-section">
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">School not on here yet? No problem.</h2>
        <div className="mt-8 grid md:grid-cols-2 gap-8">
          <div className="nb-card p-6 hover:-translate-y-1 transition-transform duration-200" data-testid="standalone-parents-card">
            <div className="w-12 h-12 rounded-xl border-2 border-zinc-900 bg-[#F9A8D4] flex items-center justify-center mb-4">
              <HeartHandshake className="w-6 h-6" />
            </div>
            <h3 className="font-display font-bold text-xl">For parents</h3>
            <p className="mt-2 text-zinc-600 text-sm leading-relaxed">
              Build your child's timetable yourself with the same builder schools use. £9.99/year or £39.99 for six years.
              Brothers and sisters welcome — extra children are £5/year each, or £20 each on the six-year plan.
            </p>
            <Link to="/parent/auth" data-testid="standalone-parent-cta" className="nb-btn bg-[#F9A8D4] px-4 py-2 mt-5 inline-flex text-sm">
              I'm a parent <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="nb-card p-6 hover:-translate-y-1 transition-transform duration-200" data-testid="standalone-students-card">
            <div className="w-12 h-12 rounded-xl border-2 border-zinc-900 bg-[#7DD3FC] flex items-center justify-center mb-4">
              <GraduationCap className="w-6 h-6" />
            </div>
            <h3 className="font-display font-bold text-xl">For students</h3>
            <p className="mt-2 text-zinc-600 text-sm leading-relaxed">
              At college, uni or learning as an adult? Keep your own timetable, exams and study days in one place.
              Same simple pricing: £9.99/year or £39.99 for six years.
            </p>
            <Link to="/parent/auth?type=student" data-testid="standalone-student-cta" className="nb-btn bg-[#7DD3FC] px-4 py-2 mt-5 inline-flex text-sm">
              I'm a student <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t-2 border-zinc-900 bg-[#FDFBF7]">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="font-display font-black text-lg">MyTimetable.co.uk</span>
          <span className="text-sm text-zinc-500" data-testid="footer-text">{cms.footer_text}</span>
        </div>
      </footer>
    </div>
  );
}
